import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;
  const [activeSlug, setActiveSlug] = useState(slug || '');

  if (!category) {
    return <NotFound />;
  }

  const articles = getArticlesByCategory(category.slug);
  const activeCategory = categoryMap[activeSlug] || category;
  const activeArticles = getArticlesByCategory(activeCategory.slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 姐姐的朋友`,
    description: category.description,
    url: `https://jiejiefriends.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '姐姐的朋友',
      url: 'https://jiejiefriends.com',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: activeArticles.map((art, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://jiejiefriends.com/article/${art.slug}`,
        name: art.title,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-12 md:py-16">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mt-3">
              {category.name}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              {category.description}
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-4">
          <div className="flex items-center gap-2 flex-wrap pb-4 border-b border-background-200/70">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
                <span className="ml-1.5 text-xs opacity-70">({cat.articleCount})</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto py-10 md:py-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden transition-all hover:border-primary-200 cursor-pointer"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
                    <span>{article.date}</span>
                    <span>·</span>
                    <span>{article.readTime}</span>
                  </div>
                  <h2 className="font-heading text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                    {article.title}
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {article.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-600 text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}